import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  formatMoneyMinor,
  invoiceCreateSchema,
  invoiceUpdateSchema,
  type InvoiceDetailRow,
  type MatterRow,
} from "@themislex/shared";
import { ApiError, api } from "../../api/client";
import { useApi } from "../../api/hooks";
import { amountToNumber } from "../../lib/parse";
import {
  Button,
  Card,
  ErrorNote,
  Input,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from "../../components/ui";

interface ItemDraft {
  description: string;
  quantity: string;
  unitAmount: string;
}

const EMPTY_ITEM: ItemDraft = { description: "", quantity: "1", unitAmount: "" };

function defaultDueDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + 14);
  return d.toISOString().slice(0, 10);
}

/** Wrapper: loads the invoice (edit) and the matters list (create). */
export default function InvoiceForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const existing = useApi<{ invoice: InvoiceDetailRow }>(
    isEdit ? `/api/invoices/${id}` : null,
  );
  const matters = useApi<{ matters: MatterRow[] }>(isEdit ? null : "/api/matters");

  if (isEdit) {
    if (existing.loading) {
      return (
        <div className="flex justify-center py-16 text-muted">
          <Spinner className="h-6 w-6" />
        </div>
      );
    }
    if (existing.error || !existing.data) {
      return <ErrorNote>{existing.error ?? "Invoice not found."}</ErrorNote>;
    }
    if (existing.data.invoice.displayStatus === "paid") {
      return (
        <>
          <PageHeader title="Edit invoice" />
          <Card>
            <ErrorNote>
              This invoice is already settled. Record a correction as a new
              invoice instead of editing a paid one.
            </ErrorNote>
            <div className="mt-4">
              <Link to={`/desk/invoices/${id}`} className="btn btn-secondary no-underline">
                Back to invoice
              </Link>
            </div>
          </Card>
        </>
      );
    }
    return <InvoiceFormFields key={id} invoice={existing.data.invoice} matters={null} defaultMatterId="" />;
  }

  return <InvoiceFormWrapper matters={matters} />;
}

function InvoiceFormWrapper({
  matters,
}: {
  matters: { loading: boolean; error?: string; data?: { matters: MatterRow[] } };
}) {
  const [params] = useSearchParams();
  if (matters.loading) {
    return (
      <div className="flex justify-center py-16 text-muted">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (matters.error || !matters.data) {
    return <ErrorNote>{matters.error ?? "Matters could not be loaded."}</ErrorNote>;
  }
  return (
    <InvoiceFormFields
      key="new"
      invoice={null}
      matters={matters.data.matters}
      defaultMatterId={params.get("matterId") ?? ""}
    />
  );
}

function InvoiceFormFields({
  invoice,
  matters,
  defaultMatterId,
}: {
  invoice: InvoiceDetailRow | null;
  matters: MatterRow[] | null;
  defaultMatterId: string;
}) {
  const navigate = useNavigate();
  const isEdit = Boolean(invoice);

  const [matterId, setMatterId] = useState(invoice?.matterId ?? defaultMatterId);
  const [status, setStatus] = useState<"draft" | "pending">(
    invoice?.status === "draft" ? "draft" : "pending",
  );
  const [dueDate, setDueDate] = useState(invoice?.dueDate ?? defaultDueDate());
  const [notes, setNotes] = useState(invoice?.notes ?? "");
  const [mode, setMode] = useState<"items" | "amount">(
    invoice && invoice.items.length === 0 ? "amount" : "items",
  );
  const [items, setItems] = useState<ItemDraft[]>(() =>
    invoice && invoice.items.length > 0
      ? invoice.items.map((item) => ({
          description: item.description,
          quantity: String(item.quantity),
          unitAmount: (item.unitAmountMinor / 100).toFixed(2),
        }))
      : [{ ...EMPTY_ITEM }],
  );
  const [singleDescription, setSingleDescription] = useState(
    invoice?.items[0]?.description ?? "Professional fees",
  );
  const [singleAmount, setSingleAmount] = useState(
    invoice && invoice.items.length === 0 ? (invoice.amountMinor / 100).toFixed(2) : "",
  );

  const [error, setError] = useState<string>();
  const [rowErrors, setRowErrors] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const totalMinor = useMemo(() => {
    if (mode === "amount") {
      return Math.round((amountToNumber(singleAmount) ?? 0) * 100);
    }
    return items.reduce((sum, item) => {
      const qty = Number(item.quantity) || 0;
      const unit = (amountToNumber(item.unitAmount) ?? 0) * 100;
      return sum + Math.round(qty * unit);
    }, 0);
  }, [mode, items, singleAmount]);

  const updateItem = (index: number, patch: Partial<ItemDraft>) =>
    setItems((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const parseItems = ():
    | { ok: true; value: { description: string; quantity: number; unitAmount: number }[] }
    | { ok: false; errors: string[] } => {
    const errors: string[] = [];
    const value = items.map((item, index) => {
      const quantity = item.quantity.trim() === "" ? 1 : Number(item.quantity);
      const unitAmount = amountToNumber(item.unitAmount);
      if (item.description.trim() === "") errors[index] = "Description is required";
      else if (!Number.isFinite(quantity) || quantity <= 0)
        errors[index] = "Quantity must be a positive number";
      else if (unitAmount === undefined) errors[index] = "Enter a valid amount";
      return {
        description: item.description.trim(),
        quantity,
        unitAmount: unitAmount ?? 0,
      };
    });
    if (errors.some(Boolean)) return { ok: false, errors };
    return { ok: true, value };
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setRowErrors([]);

    if (mode === "items") {
      const parsedItems = parseItems();
      if (!parsedItems.ok) {
        setRowErrors(parsedItems.errors);
        return;
      }
      if (totalMinor <= 0) {
        setError("The invoice total must be greater than zero.");
        return;
      }
      const payload = isEdit
        ? { dueDate: dueDate || null, notes, items: parsedItems.value }
        : {
            matterId,
            status,
            dueDate: dueDate || undefined,
            notes: notes || undefined,
            items: parsedItems.value,
          };
      await save(payload);
    } else {
      const amount = amountToNumber(singleAmount);
      if (amount === undefined || amount <= 0) {
        setError("Enter a valid invoice amount.");
        return;
      }
      if (isEdit) {
        if (singleDescription.trim() === "") {
          setError("Enter a description for the charge.");
          return;
        }
        await save({
          dueDate: dueDate || null,
          notes,
          items: [{ description: singleDescription.trim(), quantity: 1, unitAmount: amount }],
        });
      } else {
        await save({
          matterId,
          status,
          dueDate: dueDate || undefined,
          notes: notes || undefined,
          amount,
        });
      }
    }
  };

  const save = async (payload: unknown) => {
    const schema = isEdit ? invoiceUpdateSchema : invoiceCreateSchema;
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Please review the invoice details.");
      return;
    }
    setBusy(true);
    try {
      const path = isEdit ? `/api/invoices/${invoice?.id}` : "/api/invoices";
      const body = await api<{ invoice: InvoiceDetailRow }>(path, {
        method: isEdit ? "PUT" : "POST",
        json: parsed.data,
      });
      navigate(`/desk/invoices/${body.invoice.id}`);
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not save the invoice.");
    } finally {
      setBusy(false);
    }
  };

  const selectedMatter = matters?.find((m) => m.id === matterId);

  return (
    <>
      <PageHeader
        title={isEdit ? `Edit ${invoice?.invoiceNumber ?? "invoice"}` : "New invoice"}
        subtitle={
          isEdit
            ? "Line items replace the previous set; the total is recalculated."
            : "Raise an invoice against a matter. The client sees it immediately unless saved as a draft."
        }
        actions={
          <Link
            to={isEdit ? `/desk/invoices/${invoice?.id}` : "/desk/invoices"}
            className="btn btn-secondary no-underline"
          >
            Cancel
          </Link>
        }
      />

      <form onSubmit={submit} noValidate className="mx-auto max-w-4xl space-y-6">
        <Card title="Invoice details">
          {error && (
            <div className="mb-4">
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {isEdit ? (
              <>
                <Input label="Matter" value={invoice?.matterTitle ?? ""} disabled />
                <Input label="Invoice number" value={invoice?.invoiceNumber ?? ""} disabled />
              </>
            ) : (
              <div className="sm:col-span-2">
                <label className="label" htmlFor="invoice-matter">
                  Matter
                </label>
                <select
                  id="invoice-matter"
                  className="input"
                  required
                  value={matterId}
                  onChange={(e) => setMatterId(e.target.value)}
                >
                  <option value="">Select a matter…</option>
                  {matters?.map((matter) => (
                    <option key={matter.id} value={matter.id}>
                      {matter.title} — {matter.clientName}
                    </option>
                  ))}
                </select>
                {selectedMatter && (
                  <p className="mt-1 text-xs text-muted">
                    Client: {selectedMatter.clientName}
                  </p>
                )}
              </div>
            )}
            <Input
              label="Due date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              hint="Leave blank to omit a due date."
            />
            <Select
              label="Save as"
              value={status}
              onChange={(e) => setStatus(e.target.value as "draft" | "pending")}
              hint={status === "draft" ? "Drafts are invisible to the client." : undefined}
              disabled={isEdit}
            >
              <option value="pending">Issue to client</option>
              <option value="draft">Draft (internal)</option>
            </Select>
          </div>
        </Card>

        <Card title="Charges">
          <div className="mb-4 flex gap-2" role="group" aria-label="Charge entry mode">
            <button
              type="button"
              className={`btn ${mode === "items" ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setMode("items")}
              aria-pressed={mode === "items"}
            >
              Itemised
            </button>
            <button
              type="button"
              className={`btn ${mode === "amount" ? "btn-primary" : "btn-secondary"}`}
              onClick={() => setMode("amount")}
              aria-pressed={mode === "amount"}
            >
              Single amount
            </button>
          </div>

          {mode === "items" ? (
            <div>
              <div className="mb-2 grid grid-cols-12 gap-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted">
                <span className="col-span-6">Description</span>
                <span className="col-span-2">Quantity</span>
                <span className="col-span-2">Unit amount (₹)</span>
                <span className="col-span-2 text-right">Amount</span>
              </div>
              <div className="space-y-2">
                {items.map((item, index) => {
                  const lineTotal = Math.round(
                    (Number(item.quantity) || 0) *
                      (amountToNumber(item.unitAmount) ?? 0) *
                      100,
                  );
                  return (
                    <div key={index} className="grid grid-cols-12 items-start gap-2">
                      <div className="col-span-6">
                        <input
                          className="input"
                          aria-label={`Line ${index + 1} description`}
                          placeholder="e.g. Appearance before District Judge"
                          value={item.description}
                          onChange={(e) => updateItem(index, { description: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input"
                          aria-label={`Line ${index + 1} quantity`}
                          inputMode="decimal"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, { quantity: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          className="input"
                          aria-label={`Line ${index + 1} unit amount`}
                          inputMode="decimal"
                          placeholder="0.00"
                          value={item.unitAmount}
                          onChange={(e) => updateItem(index, { unitAmount: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2 flex items-center gap-1">
                        <span className="w-full pt-2 text-right text-sm tabular-nums">
                          {formatMoneyMinor(lineTotal)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger px-2 py-1 text-xs"
                          aria-label={`Remove line ${index + 1}`}
                          disabled={items.length === 1}
                          onClick={() =>
                            setItems((rows) => rows.filter((_, i) => i !== index))
                          }
                        >
                          ✕
                        </button>
                      </div>
                      {rowErrors[index] && (
                        <p className="col-span-12 text-xs text-danger-fg" role="alert">
                          Line {index + 1}: {rowErrors[index]}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button
                type="button"
                variant="ghost"
                className="mt-3"
                onClick={() => setItems((rows) => [...rows, { ...EMPTY_ITEM }])}
              >
                + Add line
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {isEdit && (
                <Input
                  label="Description"
                  value={singleDescription}
                  onChange={(e) => setSingleDescription(e.target.value)}
                />
              )}
              <Input
                label="Invoice amount (₹)"
                inputMode="decimal"
                placeholder="0.00"
                value={singleAmount}
                onChange={(e) => setSingleAmount(e.target.value)}
              />
            </div>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
            <span className="text-sm font-medium text-muted">Total</span>
            <span className="font-heading text-xl font-bold">
              {formatMoneyMinor(totalMinor)}
            </span>
          </div>
        </Card>

        <Card title="Notes on invoice (optional)">
          <Textarea
            rows={3}
            placeholder="e.g. Includes appearance on 3 hearings. Payment due within 14 days."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Card>

        <div className="flex justify-end gap-2">
          <Link
            to={isEdit ? `/desk/invoices/${invoice?.id}` : "/desk/invoices"}
            className="btn btn-secondary no-underline"
          >
            Cancel
          </Link>
          <Button type="submit" variant="primary" loading={busy}>
            {isEdit ? "Save changes" : "Create invoice"}
          </Button>
        </div>
      </form>
    </>
  );
}
